import { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Link
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion } from 'framer-motion';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import {
  MapComponent,
  NavigationMenu,
  NavigationMenuMobile,
  ContactLink
} from '../../components';
import { officesByContinent, contactEmails } from '../../utils/locationData';
import THEME from '../../constants/theme';
import { ELEMENT_SPACING } from '../../constants';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const ContactPage = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery(theme.breakpoints.down('xl'));

  // State to control expanded accordion and selected office for the map
  const [expandedOffice, setExpandedOffice] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(null); // Start with world view - no selected office

  // Handle accordion expansion and select office for map
  const handleAccordionChange = (panel, office) => (_, isExpanded) => {
    setExpandedOffice(isExpanded ? panel : null);
    setSelectedOffice(isExpanded ? office : null);
  };

  return (
    <>
      <SEO
        title={seoData.contact.title}
        content={seoData.contact.content}
        keywords={seoData.contact.keywords}
        canonical={seoData.contact.canonical}
        ogImage={seoData.contact.ogImage}
        structuredData={seoData.contact.structuredData}
      />
      <Box
        sx={{
          backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ flex: 1, position: 'relative' }}>
          {/* Map as background on mobile/tablet */}
          {isMobileOrTablet && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 0,
                pointerEvents: 'none'
              }}
            >
              <MapComponent
                showControls={false}
                selectedOffice={selectedOffice}
              />
              {/* Optional semi-transparent overlay for readability */}
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255,255,255,0.2)',
                  zIndex: 1
                }}
              />
            </Box>
          )}

          <Grid
            container
            spacing={0}
            sx={{
              padding: isMobileOrTablet ? '4vh 4vw' : '10vh 7vw',
              minHeight: '80vh',
              position: 'relative',
              pr: { xs: '7vw', md: '8vw', lg: '10vw' },
              zIndex: 2 // ensures content is above background map
            }}
          >
            {/* Main Content */}
            <Grid
              item
              xs={12}
              md={4}
              component={motion.div}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              sx={{
                ...(isMobileOrTablet && {
                  background: 'rgba(255,255,255,0.72)',
                  borderRadius: 3,
                  boxShadow: 2,
                  padding: '1.5em 1em',
                  zIndex: 3,
                  position: 'relative'
                })
              }}
            >
              {officesByContinent.map((group, groupIndex) => (
                <Box key={groupIndex} sx={{ marginBottom: '1.5rem' }}>
                  <Typography
                    variant='h6'
                    sx={{
                      color: THEME.COLORS.PRIMARY_BLUE,
                      marginBottom: '0.5rem',
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD
                    }}
                  >
                    {group.continent}
                  </Typography>
                  {group.offices.map((office, officeIndex) => {
                    const panelId = `${groupIndex}-${officeIndex}`;
                    return (
                      <Accordion
                        key={panelId}
                        expanded={expandedOffice === panelId}
                        onChange={handleAccordionChange(panelId, office)}
                        sx={{
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                          margin: 0,
                          '&::before': {
                            display: 'none'
                          },
                          borderBottom: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
                          '&.Mui-expanded': {
                            margin: 0
                          }
                        }}
                        disableGutters
                      >
                        <AccordionSummary
                          expandIcon={
                            <ExpandMoreIcon
                              sx={{ color: THEME.COLORS.PRIMARY_BLUE }}
                            />
                          }
                          sx={{
                            padding: '8px 0',
                            minHeight: '48px',
                            '&.Mui-expanded': {
                              minHeight: '48px'
                            }
                          }}
                        >
                          <Typography
                            variant='body1'
                            sx={{
                              color: THEME.COLORS.NEUTRAL_BLACK,
                              fontFamily:
                                THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                              fontWeight: office.isHeadquarters ? 600 : 400
                            }}
                          >
                            {office.name} {office.isHeadquarters && '(HQ)'}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography
                            variant='body2'
                            sx={{
                              whiteSpace: 'pre-line',
                              color: THEME.COLORS.NEUTRAL_BLACK,
                              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
                            }}
                          >
                            {office.details}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              ))}

              <Box sx={{ marginTop: '2rem' }}>
                <Typography
                  variant='h6'
                  sx={{
                    color: THEME.COLORS.PRIMARY_BLUE,
                    marginBottom: '0.5rem',
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                    fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD
                  }}
                >
                  Email Contacts
                </Typography>
                <List
                  sx={{
                    '> *': {
                      marginBottom: '0.5rem',
                      paddingTop: '0',
                      paddingBottom: '0'
                    }
                  }}
                >
                  {contactEmails.map((contact, index) => (
                    <ListItem key={index} disableGutters sx={{ padding: 0 }}>
                      <ListItemText
                        primary={
                          <Typography
                            variant='body1'
                            sx={{
                              color: THEME.COLORS.NEUTRAL_BLACK,
                              fontFamily:
                                THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                              fontWeight: 500
                            }}
                          >
                            {contact.label}
                          </Typography>
                        }
                        secondary={
                          <Link
                            href={`mailto:${contact.email}`}
                            sx={{
                              color: THEME.COLORS.NEUTRAL_GRAY_DARK,
                              textDecoration: 'none',
                              fontFamily:
                                THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                              '&:hover': {
                                color: THEME.COLORS.PRIMARY_BLUE,
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {contact.email}
                          </Link>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                <Box mt={ELEMENT_SPACING}>
                  <Typography
                    variant='body2'
                    sx={{
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                      fontSize: '1.2rem',
                      color: THEME.COLORS.PRIMARY_BLUE,
                      lineHeight: 1.6
                    }}
                  >
                    Talk to an Expert –&nbsp;
                    <ContactLink
                      style={{
                        color: THEME.COLORS.NEUTRAL_GRAY,
                        textDecoration: 'underline',
                        fontSize: '1rem'
                      }}
                    >
                      Schedule Your meeting
                    </ContactLink>
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Map at right for desktop */}
            {!isMobileOrTablet && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                sx={{
                  position: 'absolute',
                  top: '10%',
                  right: '18vw',
                  width: '45%',
                  height: '75%',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  zIndex: 1
                }}
              >
                <MapComponent
                  selectedOffice={selectedOffice}
                  showControls={true}
                />
              </Box>
            )}
          </Grid>
        </Box>

        {/* Navigation Menus */}
        {isDesktop ? (
          <NavigationMenuMobile textColor='#000000' />
        ) : (
          <NavigationMenu textColor='#000000' />
        )}
      </Box>
    </>
  );
};

export default ContactPage;
